/**
 * Safari Scroll Enhancement
 * 
 * macOS SAFARI INSTANT SCROLL MODE:
 * Disables smooth scroll animations in macOS Safari (desktop) to eliminate stuttering.
 * iOS Safari works fine with smooth scroll, so it's excluded.
 * 
 * Toggle FORCE_INSTANT_SCROLL to choose between:
 * - true: Instant scroll (no animation) - recommended for macOS Safari
 * - false: Smooth scroll with image preload/decode (complex, may still stutter)
 */

(function() {
	const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
	const isMacOSSafari = isSafari && !isIOS
	
	if (!isMacOSSafari) {
		return
	}

	const FORCE_INSTANT_SCROLL = true

	const decodeImage = async (img) => {
		if (!img || img.complete) {
			return Promise.resolve()
		}
		
		try {
			if ('decode' in img) {
				await img.decode()
			} else {
				await new Promise((resolve, reject) => {
					if (img.complete) {
						resolve()
					} else {
						img.onload = resolve
						img.onerror = reject
					}
				})
			}
		} catch (error) {
			console.warn('Image decode failed:', error)
		}
	}

	const preloadAndDecodeImages = async (cells, currentIndex) => {
		if (!cells || cells.length === 0) {
			return
		}

		const indicesToPreload = [
			Math.max(0, currentIndex - 1),
			currentIndex,
			Math.min(cells.length - 1, currentIndex + 1)
		]

		const imagesToDecode = []
		
		indicesToPreload.forEach(index => {
			const cell = cells[index]
			if (!cell) return
			
			const images = cell.querySelectorAll('img[loading="lazy"]')
			images.forEach(img => {
				img.removeAttribute('loading')
				imagesToDecode.push(img)
			})
			
			const videos = cell.querySelectorAll('video[preload="none"]')
			videos.forEach(video => {
				video.setAttribute('preload', 'metadata')
			})
		})

		await Promise.all(imagesToDecode.map(img => decodeImage(img)))
	}

	const roundScrollPosition = (value) => {
		return Math.round(value)
	}

	function initScrollEnhancement() {
		const scrollCarousels = document.querySelectorAll('scroll-carousel')
		
		scrollCarousels.forEach(carousel => {
			let isScrolling = false
			let scrollEndTimeout = null
			let lastScrollLeft = 0
			
			carousel.style.transform = 'translateZ(0)'
			carousel.style.backfaceVisibility = 'hidden'
			carousel.style.webkitBackfaceVisibility = 'hidden'
			
			const originalScrollTo = carousel.scrollTo.bind(carousel)
			
			carousel.scrollTo = async function(options) {
				if (!options) {
					return originalScrollTo(options)
				}

				const roundedOptions = { ...options }
				if (typeof roundedOptions.left === 'number') {
					roundedOptions.left = roundScrollPosition(roundedOptions.left)
				}
				if (typeof roundedOptions.top === 'number') {
					roundedOptions.top = roundScrollPosition(roundedOptions.top)
				}

				if (FORCE_INSTANT_SCROLL) {
					roundedOptions.behavior = 'auto'
					originalScrollTo(roundedOptions)
					return
				}

				if (roundedOptions.behavior === 'smooth') {
					if (isScrolling) {
						carousel.style.scrollBehavior = 'auto'
						originalScrollTo({ left: lastScrollLeft, behavior: 'auto' })
						
						await new Promise(resolve => requestAnimationFrame(resolve))
					}

					const cells = carousel.querySelectorAll('.product-gallery__media:not([hidden])')
					const currentIndex = carousel.selectedIndex || 0
					
					await preloadAndDecodeImages(cells, currentIndex)
					
					await new Promise(resolve => requestAnimationFrame(resolve))
					
					carousel.style.removeProperty('scroll-behavior')
				}
				
				originalScrollTo(roundedOptions)
				lastScrollLeft = roundedOptions.left || carousel.scrollLeft
				
				if (roundedOptions.behavior === 'smooth') {
					isScrolling = true
					
					if (scrollEndTimeout) {
						clearTimeout(scrollEndTimeout)
					}
					
					const handleScrollEnd = () => {
						isScrolling = false
						carousel.removeEventListener('scrollend', handleScrollEnd)
					}
					
					carousel.addEventListener('scrollend', handleScrollEnd, { once: true })
					
					scrollEndTimeout = setTimeout(() => {
						isScrolling = false
					}, 600)
				}
			}

			const handleScroll = () => {
				if (!isScrolling) {
					lastScrollLeft = carousel.scrollLeft
				}
			}
			
			carousel.addEventListener('scroll', handleScroll, { passive: true })

			const cells = carousel.querySelectorAll('.product-gallery__media:not([hidden])')
			if (cells.length > 0) {
				preloadAndDecodeImages(cells, 0)
			}
		})
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initScrollEnhancement)
	} else {
		initScrollEnhancement()
	}

	const observer = new MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			mutation.addedNodes.forEach((node) => {
				if (node.nodeType === 1 && node.tagName === 'SCROLL-CAROUSEL') {
					setTimeout(() => initScrollEnhancement(), 100)
				}
			})
		})
	})

	observer.observe(document.body, {
		childList: true,
		subtree: true
	})
})()
